import React, {useEffect, useState, useMemo} from 'react';
import {KeywordMapping} from './htmlParserCombined';
import EventSearch from '../components/EventSearch';
import PastPerformanceStyler from '../components/PastPerformanceStyler';
import EventMapView from '../components/EventMapView';
import YouAreHereCard, {YouAreHereCardProps} from '../components/YouAreHereCard';
import OfferCard from '../components/Articles/Offers/OfferCard';
import EventCard from '../components/Articles/EventCard';
import PlaceCard from '../components/Articles/PlaceCard';
import PlaceListings from '../components/PlaceListings';
import FarmersMarketContent from '../pages/Event/About/FarmersMarketContent';
import ComicConContent from '../pages/Event/About/ComicConContent';
import CuratedEventContent from '../pages/Event/About/CuratedEventContent';
import TasteEventContent from '../pages/Event/About/TasteEventContent';
import {Offer, Event, Place} from '../types/article';
import {fetchOffer, fetchEvent, fetchPlace} from '../endpoints/api';
import {SpinnerLoader} from '../components/Loader';
import MeetUpCard from '../components/Articles/MeetUpCard';

// Global state to share map places data between mapView and youAreHere components
// Using let instead of useState to avoid React render cycles as this is used across components
let sharedMapPlacesData: YouAreHereCardProps['mapPlaces'] = undefined;

export const setMapPlacesData = (data: YouAreHereCardProps['mapPlaces']) => {
  if (!data) return;
  // Deep clone to avoid reference issues
  sharedMapPlacesData = JSON.parse(JSON.stringify(data));
};

// A flag to track if youAreHere card has been dynamically inserted
let youAreHereDynamicInserted = false;

// Optionally track a map center that was passed
let sharedMapCenter: {latitude: number; longitude: number} | undefined = undefined;

export const setMapCenter = (center: {latitude: number; longitude: number} | undefined) => {
  if (!center) return;
  sharedMapCenter = center;
};

// Function to get the closest place name based on user location
export const getClosestPlaceName = (): string | null => {
  if (!sharedMapPlacesData || sharedMapPlacesData.length === 0) return null;
  return sharedMapPlacesData[0]?.name || null;
};

export const eventKeywordMappings: KeywordMapping[] = [
  {
    keyword: '[[offerCard]]',
    component: (props) => {
      const [offer, setOffer] = useState<Offer | null>(null);
      const [isLoading, setIsLoading] = useState(false);
      const [error, setError] = useState<Error | null>(null);

      // Extract the offer ID from the props - Convert to number if it's a string
      const offerId = props?.id ? Number(props.id) : null;

      // Function to find the offer in the event.offers array
      const findOfferInEvent = (eventOffers: Offer[], id: number): Offer | null => {
        if (!eventOffers || !Array.isArray(eventOffers)) return null;
        return eventOffers.find((offer) => offer.id === id) || null;
      };
      // If we have event and offerId, check if the offer is in eventOffers or event.offers
      useEffect(() => {
        if (!offerId) {
          console.log('No offer ID provided');
          return;
        }

        // First check if we can find the offer in eventOffers (passed from parent)
        if (props.eventOffers && Array.isArray(props.eventOffers)) {
          const foundOffer = findOfferInEvent(props.eventOffers, offerId);
          if (foundOffer) {
            setOffer(foundOffer);
            return;
          }
        }

        // Then check if we can find the offer in event.offers
        if (props.event && props.event.offers) {
          const foundOffer = findOfferInEvent(props.event.offers, offerId);
          if (foundOffer) {
            setOffer(foundOffer);
            return;
          }
        }

        // If not found in any local data, fetch it from API
        setIsLoading(true);
        fetchOffer(offerId)
          .then((response) => {
            if (response && response.data) {
              setOffer(response.data);
            } else {
              setOffer(null);
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error fetching offer:', err);
            setError(err);
            setIsLoading(false);
          });
      }, [offerId, props.event, props.eventOffers]); // If there's an error or no offer was found, render null

      if (isLoading) return <SpinnerLoader />;

      if (error || (!offer && !isLoading)) return null; // If we have an offer, render the offer card
      return offer ? <OfferCard offer={offer} /> : null;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[eventCard]]',
    component: (props) => {
      const [event, setEvent] = useState<Event | null>(null);
      const [isLoading, setIsLoading] = useState(false);
      const [error, setError] = useState<Error | null>(null);

      // Extract the event ID from the props - Convert to number if it's a string
      const eventId = props?.id ? Number(props.id) : null;

      useEffect(() => {
        if (!eventId) {
          console.log('No event ID provided');
          return;
        }

        // Fetch the event from API
        setIsLoading(true);
        fetchEvent({id: eventId})
          .then((response) => {
            if (response && response.data) {
              setEvent(response.data);
            } else {
              setEvent(null);
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error fetching event:', err);
            setError(err);
            setIsLoading(false);
          });
      }, [eventId]);

      if (isLoading) return <SpinnerLoader />;

      // If there's an error or no event was found, render null
      if (error || (!event && !isLoading)) return null;

      // If we have an event, render the event card
      return event ? event.is_meetup ? <MeetUpCard meet-up={event} /> : <EventCard event={event} /> : null;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[placeCard]]',
    component: (props) => {
      const [place, setPlace] = useState<Place | null>(null);
      const [isLoading, setIsLoading] = useState(false);
      const [error, setError] = useState<Error | null>(null);

      // Extract the place ID from the props - Convert to number if it's a string
      const placeId = props?.id ? Number(props.id) : null;

      useEffect(() => {
        if (!placeId) {
          console.log('No place ID provided');
          return;
        }

        // Fetch the place from API
        setIsLoading(true);
        fetchPlace({id: placeId})
          .then((response) => {
            if (response && response.data) {
              setPlace(response.data);
            } else {
              setPlace(null);
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error fetching place:', err);
            setError(err);
            setIsLoading(false);
          });
      }, [placeId]);

      if (isLoading) return <SpinnerLoader />;

      // If there's an error or no place was found, render null
      if (error || (!place && !isLoading)) return null;

      // If we have a place, render the place card
      return place ? <PlaceCard place={place} /> : null;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[youAreHere]]',
    component: () => (
      <YouAreHereCard mapPlaces={sharedMapPlacesData} shouldPositionDynamically={true} />
    ),
    props: {},
  },
  {
    keyword: '[[eventSearch]]',
    component: () => <EventSearch />,
    props: {},
  },
  {
    keyword: '[[pastPerformanceStyler]]',
    component: () => <PastPerformanceStyler />,
    props: {},
  },
  {
    keyword: '[[mapView]]',
    component: (props) => {
      // Extract the JSON parameters from the props
      // Filter out React-specific props to get only the extracted JSON data
      // Example usage in HTML:
      // 1. Simple array of places:
      //    [[mapView]][{"name": "Place 1", "latitude": 32.7, "longitude": -117.1}, {"name": "Place 2", "latitude": 32.8, "longitude": -117.2}]
      // 2. Object with mapCenter and places array:
      //    [[mapView]]{"mapCenter": {"latitude": 32.75, "longitude": -117.15}, "places": [{"name": "Place 1", "latitude": 32.7, "longitude": -117.1}]}
      const {preview, setSignUpRequiredOpen, key, inline, ...extractedParams} = props || {};

      let placesData; // Handle map center extraction from the props
      if (extractedParams && typeof extractedParams === 'object' && extractedParams.mapCenter) {
        // Normalize the center coordinates
        const mapCenter = extractedParams.mapCenter;
        const normalizedCenter = {
          latitude:
            typeof mapCenter.latitude === 'string'
              ? parseFloat(mapCenter.latitude)
              : mapCenter.latitude,
          longitude:
            typeof mapCenter.longitude === 'string'
              ? parseFloat(mapCenter.longitude)
              : mapCenter.longitude,
        };

        console.log('Extracted map center from JSON:', normalizedCenter);

        // Share the map center with other components
        setMapCenter(normalizedCenter);
      }

      // More reliable extraction of place data from various structures
      if (extractedParams && Object.keys(extractedParams).length > 0) {
        try {
          // Handle string input (might occur when TinyMCE or other editors change the format)
          if (typeof extractedParams === 'string') {
            try {
              // Try to parse it as JSON
              const parsedData = JSON.parse(extractedParams.replace(/\\"/g, '"'));
              // Create a new object with the parsed data instead of reassigning the constant
              const processedParams = parsedData;

              // Continue with processed data instead of the original
              if (Array.isArray(processedParams) && processedParams.length > 0) {
                if (
                  processedParams[0] &&
                  typeof processedParams[0] === 'object' &&
                  'name' in processedParams[0]
                ) {
                  placesData = JSON.stringify(processedParams);
                  // Skip the rest of the processing since we've handled this case
                  return <EventMapView placesData={placesData} />;
                }
              } else if (typeof processedParams === 'object' && processedParams !== null) {
                if (
                  processedParams.name &&
                  (typeof processedParams.latitude === 'number' ||
                    typeof processedParams.latitude === 'string') &&
                  (typeof processedParams.longitude === 'number' ||
                    typeof processedParams.longitude === 'string')
                ) {
                  // Handle string coordinates by converting them to numbers
                  const data = {...processedParams};
                  if (typeof data.latitude === 'string') data.latitude = parseFloat(data.latitude);
                  if (typeof data.longitude === 'string')
                    data.longitude = parseFloat(data.longitude);
                  placesData = JSON.stringify(data);
                  // Skip the rest of the processing since we've handled this case
                  return <EventMapView placesData={placesData} />;
                }
              }
            } catch (parseError) {
              console.warn('Could not parse string params:', parseError);
            }
          }

          // Case 1: The parser already extracted an array of places with the expected structure
          if (Array.isArray(extractedParams) && extractedParams.length > 0) {
            const firstItem = extractedParams[0];
            if (
              typeof firstItem === 'object' &&
              firstItem !== null &&
              'name' in firstItem &&
              'latitude' in firstItem
            ) {
              placesData = JSON.stringify(extractedParams);
            }
          }
          // Case 2: Single location object with the expected properties
          else if (
            typeof extractedParams === 'object' &&
            extractedParams.name &&
            (typeof extractedParams.latitude === 'number' ||
              typeof extractedParams.latitude === 'string') &&
            (typeof extractedParams.longitude === 'number' ||
              typeof extractedParams.longitude === 'string')
          ) {
            // Handle string coordinates by converting them to numbers
            const data = {...extractedParams};
            if (typeof data.latitude === 'string') data.latitude = parseFloat(data.latitude);
            if (typeof data.longitude === 'string') data.longitude = parseFloat(data.longitude);
            placesData = JSON.stringify(data);
          }
          // Case 3: Object with numeric keys that should be treated as an array
          else if (Object.keys(extractedParams).some((key) => !isNaN(Number(key)))) {
            const arrayData = Object.values(extractedParams).filter(
              (item) =>
                typeof item === 'object' && item !== null && 'name' in item && 'latitude' in item
            );
            if (arrayData.length > 0) {
              placesData = JSON.stringify(arrayData);
            }
          }
          // Case 4: Nested array somewhere in the object structure
          else {
            // Helper function to recursively find and extract arrays of place objects
            const findPlacesArray = (obj: any): any[] | null => {
              if (!obj || typeof obj !== 'object') return null;

              // Direct check for arrays
              if (Array.isArray(obj) && obj.length > 0) {
                if (
                  typeof obj[0] === 'object' &&
                  obj[0] !== null &&
                  'name' in obj[0] &&
                  'latitude' in obj[0]
                ) {
                  return obj;
                }
              }

              // Check object properties recursively
              for (const key in obj) {
                const value = obj[key];
                if (Array.isArray(value) && value.length > 0) {
                  const firstItem = value[0];
                  if (
                    typeof firstItem === 'object' &&
                    firstItem !== null &&
                    'name' in firstItem &&
                    'latitude' in firstItem
                  ) {
                    return value;
                  }
                } else if (typeof value === 'object' && value !== null) {
                  const nestedArray = findPlacesArray(value);
                  if (nestedArray) return nestedArray;
                }
              }

              return null;
            };

            const placesArray = findPlacesArray(extractedParams);
            if (placesArray) {
              placesData = JSON.stringify(placesArray);
            } else {
              // Last resort - pass the whole structure and let EventMapView handle it
              placesData = JSON.stringify(extractedParams);
            }
          }
        } catch (error) {
          console.error('Error processing map data:', error);
          placesData = undefined;
        }
      } else {
        console.warn('No valid data extracted for mapView component');
        placesData = undefined;
      }

      // Log the data we're passing to EventMapView for debugging
      console.log('Passing data to EventMapView:', {
        placesData,
        extractedParams,
      });

      // Ensure we preserve the original structure when passing to EventMapView
      return <EventMapView placesData={placesData} />;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[placeListings]]',
    component: (props: any) => {
      // Handle different possible data structures:
      // 1. If props is an array with one element containing places and styles
      // 2. If props directly contains places and styles
      // 3. If props is an array of place objects directly
      // 4. Handle string props that might come from HTML parsing
      // 5. Handle object with numeric keys (e.g., {"0": {places: [...], styles: {...}}})

      let places: any[] = [];
      let styles: Record<string, any> = {};
      // Convert string to JSON if needed (might happen from HTML parsing)
      let processedProps = props;
      if (typeof props === 'string') {
        try {
          // Handle the backend's HTML-encoded and corrupted JSON
          let cleanedJson = props;

          // Step 1: Handle HTML entities
          cleanedJson = cleanedJson
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/&eacute;/g, 'é')
            .replace(/&ntilde;/g, 'ñ')
            .replace(/&agrave;/g, 'à')
            .replace(/&aacute;/g, 'á')
            .replace(/&iacute;/g, 'í')
            .replace(/&oacute;/g, 'ó')
            .replace(/&uacute;/g, 'ú');

          // Step 2: Handle escaped quotes
          cleanedJson = cleanedJson.replace(/\\"/g, '"');

          // Step 3: Clean line breaks that break JSON
          cleanedJson = cleanedJson.replace(/\r\n/g, ' ').replace(/\n/g, ' ').replace(/\r/g, ' ');

          // Step 4: Try to fix common HTML attribute quote issues
          // This is a simplified approach that works for most cases
          cleanedJson = cleanedJson.replace(/style=([^"\s>]+)/g, 'style="$1"');

          // Step 5: Normalize whitespace
          cleanedJson = cleanedJson.replace(/\s+/g, ' ').trim();

          processedProps = JSON.parse(cleanedJson);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          console.error('PlaceListings: Failed to parse JSON:', errorMessage);

          // If JSON parsing completely fails, show an error message instead of breaking
          return (
            <div
              style={{
                padding: '20px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '5px',
              }}>
              <h3>Unable to Display Place Listings</h3>
              <p>There was an issue parsing the place data. Please check the data format.</p>
              <details>
                <summary>Technical Details</summary>
                <p>Error: {errorMessage}</p>
              </details>
            </div>
          );
        }
      }

      // Handle object with numeric keys (case 5)
      if (processedProps && typeof processedProps === 'object' && !Array.isArray(processedProps)) {
        // Check for object with numeric keys that contain places data
        const numericKeys = Object.keys(processedProps).filter((key) => !isNaN(Number(key)));
        if (numericKeys.length > 0) {
          // Use the first numeric key's data (usually "0")
          const firstKey = numericKeys[0];
          if (processedProps[firstKey]?.places && Array.isArray(processedProps[firstKey].places)) {
            places = processedProps[firstKey].places;
            styles = processedProps[firstKey].styles || {};
            return <PlaceListings places={places} styles={styles} />;
          }
        }
      } // Now handle the different data structures

      if (Array.isArray(processedProps) && processedProps.length === 1) {
        if (processedProps[0]?.places) {
          // Case 1: Array with single object that has places property
          places = processedProps[0].places;
          styles = processedProps[0].styles || {};
        } else if (
          typeof processedProps[0] === 'object' &&
          'id' in processedProps[0] &&
          'name' in processedProps[0]
        ) {
          // Case 3A: Array with place objects directly, but wrapped in an extra array
          places = processedProps[0];
        }
      } else if (processedProps?.places && Array.isArray(processedProps.places)) {
        // Case 2: Direct object with places property (THIS IS OUR CASE)
        places = processedProps.places;
        styles = processedProps.styles || {};
      } else if (Array.isArray(processedProps)) {
        // Case 3: Array of place objects directly
        // Check if first item has place-like properties
        if (
          processedProps.length > 0 &&
          typeof processedProps[0] === 'object' &&
          'name' in processedProps[0]
        ) {
          places = processedProps;
        }
      } else if (
        processedProps &&
        typeof processedProps === 'object' &&
        !Array.isArray(processedProps)
      ) {
        // Check if it's a place-like object (might be a single place)
        if ('name' in processedProps && 'id' in processedProps) {
          places = [processedProps];
        }

        // Special handling for event property that might contain the data
        if (processedProps.event && typeof processedProps.event === 'object') {
          // Check if there's data embedded in the event object
          if (processedProps.event.places && Array.isArray(processedProps.event.places)) {
            places = processedProps.event.places;
            styles = processedProps.event.styles || {};
          }
        }
      } // Final validation
      if (!places || !Array.isArray(places) || places.length === 0) {
        console.warn('No valid places data provided for placeListings component', processedProps);
        return null;
      }

      return (
        <PlaceListings
          places={places}
          styles={styles}
          event={processedProps.event}
          eventOffers={processedProps.eventOffers}
        />
      );
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[northparkFarmersMarket]]',
    component: (props) => {
      // Extract event IDs from props if provided in JSON format
      // Example usage: [[northparkFarmersMarket]][123, 456, 789]
      const eventIds = useMemo(() => {
        let ids: number[] = [];

        try {
          if (Array.isArray(props)) {
            // If props is directly an array of numbers
            ids = props
              .filter((id) => typeof id === 'number' || !isNaN(Number(id)))
              .map((id) => Number(id));
          } else if (props && typeof props === 'object' && props.eventIds) {
            // If props is an object with an eventIds property
            ids = Array.isArray(props.eventIds) ? props.eventIds.map((id) => Number(id)) : [];
          } else if (props && Object.keys(props).length > 0) {
            // Check if props contains numeric keys (parsed from JSON array)
            const numericKeys = Object.keys(props).filter((key) => !isNaN(Number(key)));
            if (numericKeys.length > 0) {
              ids = numericKeys.map((key) => Number(props[key])).filter((id) => !isNaN(id));
            }
          }
        } catch (error) {
          console.warn('Error parsing event IDs for northparkFarmersMarket:', error);
        }

        return ids;
      }, [
        // Create stable dependencies by stringifying the actual content
        Array.isArray(props) ? props.join(',') : '',
        props?.eventIds ? JSON.stringify(props.eventIds) : '',
        props && typeof props === 'object'
          ? JSON.stringify(
              Object.keys(props)
                .filter((key) => !isNaN(Number(key)))
                .sort()
            )
          : '',
      ]);

      return <FarmersMarketContent eventIds={eventIds} event={props.event} />;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[specialHeader]]',
    component: () => {
      // This keyword is used as a flag to indicate events should use special header layout
      // It doesn't render any visible component, just gets parsed and removed from description
      return null;
    },
    props: {},
  },
  {
    keyword: '[[farmersMarketHeader]]',
    component: () => {
      // This keyword is used as a flag to indicate events should use farmers market header layout
      // It doesn't render any visible component, just gets parsed and removed from description
      return null;
    },
    props: {},
  },
  {
    keyword: '[[prideNavigator]]',
    component: (props) => {
      // Check if Pride content should be visible based on date and environment
      const isPrideVisible = useMemo(() => {
        // @ts-ignore - process.env is defined at build time
        const isProduction = process.env.NODE_ENV === 'production';

        // In non-production environments, always show for testing
        if (!isProduction) {
          return true;
        }

        // In production, only show during Pride weekend (July 18-20, 2025)
        const now = new Date();
        const currentYear = now.getFullYear(); // July 18, 2025 (month is 0-indexed)

        const prideEnd = new Date(currentYear, 6, 20, 23, 59, 59); // July 20, 2025, end of day

        return now <= prideEnd;
      }, []);

      // If Pride is not visible, return null (don't render anything)
      if (!isPrideVisible) {
        return null;
      }

      // Check if we're on a preview page - if so, show login/signup message
      const isPreviewPage = useMemo(() => {
        return window.location.pathname.includes('/preview');
      }, []);

      // If on preview page, show sign-up message instead of content
      if (isPreviewPage) {
        return (
          <div
            style={{
              padding: '24px',
              margin: '16px 0',
              backgroundColor: '#f8f9fa',
              border: '2px solid #e40475',
              borderRadius: '12px',
              textAlign: 'center',
              fontFamily: 'inherit',
            }}>
            <div
              style={{
                fontSize: '24px',
                marginBottom: '8px',
              }}>
              🏳️‍🌈
            </div>
            <h3
              style={{
                color: '#e40475',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 12px 0',
              }}>
              Pride Navigator
            </h3>
            <p
              style={{
                color: '#666',
                fontSize: '14px',
                margin: '0 0 16px 0',
                lineHeight: '1.4',
              }}>
              Sign up or log in to explore events, venues, and community recommendations
            </p>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}>
              <button
                style={{
                  backgroundColor: '#e40475',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                onClick={() => (window.location.href = '/soft-check')}>
                Sign Up
              </button>
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#e40475',
                  border: '1px solid #e40475',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  (window.location.href = `/login?redirectTo=/events/${props.event?.id || ''}`)
                }>
                Log In
              </button>
            </div>
          </div>
        );
      }

      // Extract topic ID from props - should be the San Diego Pride tag/topic ID
      const topicId = useMemo(() => {
        // If props contains a topicId, use it
        if (props && typeof props === 'object' && props.topicId) {
          return String(props.topicId);
        }

        // If props is a string, treat it as the topic ID
        if (typeof props === 'string') {
          return props;
        }

        // If props is an array with one element, use that
        if (Array.isArray(props) && props.length === 1) {
          return String(props[0]);
        }

        // Default to a known Pride topic ID - CHANGE THIS TO YOUR ACTUAL PRIDE TOPIC ID
        // Common possibilities: 'pride', 'san-diego-pride', 'pride-2024', 'pride-festival'
        // Check your admin system for the correct topic/tag ID
        return 'pride'; // Updated to more likely topic ID
      }, [
        props?.topicId,
        typeof props === 'string' ? props : '',
        Array.isArray(props) ? props.join(',') : '',
      ]);

      // Pride-specific configuration with vibrant Pride theme colors
      const prideConfig = useMemo(
        () => ({
          layout: 'list' as const,
          sections: {
            events: {
              enabled: true,
              title: 'Events',
              icon: '🏳️‍🌈',
              backgroundColor: '#e40475',
              textColor: 'white',
              borderColor: '#e40475',
              hoverColor: '#c2035e',
              circleColor: '#e40475',
              description: 'Join the celebration with events and festivities',
              sortBy: 'distance' as const,
            },
            meetups: {
              enabled: true,
              title: 'Community Meet-ups',
              icon: '🤝',
              backgroundColor: '#0082ca',
              textColor: 'white',
              borderColor: '#0082ca',
              hoverColor: '#006ba3',
              circleColor: '#0082ca',
              description: 'Connect with the community at these gatherings',
              sortBy: 'distance' as const,
            },
            offers: {
              enabled: true,
              title: 'Offers & Deals',
              icon: '💰',
              backgroundColor: '#f39c12',
              textColor: 'white',
              borderColor: '#f39c12',
              hoverColor: '#e67e22',
              circleColor: '#f39c12',
              description: 'Special deals and discounts',
              sortBy: 'distance' as const,
            },
            partners: {
              enabled: true,
              title: 'Walkabout Partners',
              icon: '🤝',
              backgroundColor: '#00a550',
              textColor: 'white',
              borderColor: '#00a550',
              hoverColor: '#008f44',
              circleColor: '#00a550',
              description: 'Businesses and organizations',
              sortBy: 'distance' as const,
            },
            places: {
              enabled: true,
              title: 'Places',
              icon: '📍',
              backgroundColor: '#732982',
              textColor: 'white',
              borderColor: '#732982',
              hoverColor: '#5a1f66',
              circleColor: '#732982',
              description: 'Great places and spots to check out',
              sortBy: 'distance' as const,
            },
          },
        }),
        []
      );

      return <CuratedEventContent topicId={topicId} config={prideConfig} event={props.event} />;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[comicCon]]',
    component: (props) => {
      // Extract event IDs from props if provided in JSON format
      // Example usage: [[comicCon]][123, 456, 789]
      const eventIds = useMemo(() => {
        let ids: number[] = [];

        try {
          if (Array.isArray(props)) {
            ids = props.filter((id) => !isNaN(Number(id))).map((id) => Number(id));
          } else if (props && typeof props === 'object' && props.eventIds) {
            ids = Array.isArray(props.eventIds) ? props.eventIds : [];
          } else if (props && Object.keys(props).length > 0) {
            const numericKeys = Object.keys(props).filter((key) => !isNaN(Number(key)));
            if (numericKeys.length > 0) {
              ids = numericKeys.map((key) => Number(props[key])).filter((id) => !isNaN(id));
            }
          }
        } catch (error) {
          console.warn('Error parsing event IDs for comicCon:', error);
        }

        return ids;
      }, [
        // Create stable dependencies by stringifying the actual content
        Array.isArray(props) ? props.join(',') : '',
        props?.eventIds ? JSON.stringify(props.eventIds) : '',
        props && typeof props === 'object'
          ? JSON.stringify(
              Object.keys(props)
                .filter((key) => !isNaN(Number(key)))
                .sort()
            )
          : '',
      ]);

      return <ComicConContent eventIds={eventIds} event={props.event} />;
    },
    props: {},
    extractParams: true,
  },
  {
    keyword: '[[tasteOfNorthPark]]',
    component: (props) => {
      // Extract topic ID and optional target location from props
      // Example usage: [[tasteOfNorthPark]]["your-topic-id"]
      // Or with location: [[tasteOfNorthPark]]{"topicId": "your-topic-id", "targetLocation": {"latitude": 32.7467, "longitude": -117.1310}}
      const config = useMemo(() => {
        let topicId: string | undefined;
        let targetLocation: {latitude: number; longitude: number} | undefined;
        let eventIds: number[] = [];

        try {
          if (typeof props === 'string') {
            // Simple string topic ID
            topicId = props;
          } else if (Array.isArray(props)) {
            // Array format - first element is topic ID, rest could be event IDs
            if (props.length > 0) {
              topicId = String(props[0]);
              // Check if remaining elements are event IDs
              eventIds = props.slice(1).filter(id => !isNaN(Number(id))).map(id => Number(id));
            }
          } else if (props && typeof props === 'object') {
            // Object format with topicId and optional targetLocation
            topicId = props.topicId ? String(props.topicId) : undefined;
            targetLocation = props.targetLocation;
            eventIds = Array.isArray(props.eventIds) ? props.eventIds : [];
            
            // Handle numeric keys (parsed from JSON array)
            const numericKeys = Object.keys(props).filter((key) => !isNaN(Number(key)));
            if (numericKeys.length > 0) {
              topicId = topicId || String(props[numericKeys[0]]);
              eventIds = numericKeys.slice(1).map((key) => Number(props[key])).filter((id) => !isNaN(id));
            }
          }
        } catch (error) {
          console.warn('Error parsing props for tasteOfNorthPark:', error);
        }

        return {
          topicId,
          targetLocation,
          eventIds,
        };
      }, [
        typeof props === 'string' ? props : '',
        Array.isArray(props) ? props.join(',') : '',
        props && typeof props === 'object' ? JSON.stringify(props) : '',
      ]);

      // Don't render if no topic ID is provided
      if (!config.topicId) {
        console.warn('No topic ID provided for tasteOfNorthPark component');
        return null;
      }

      return (
        <TasteEventContent
          topicId={config.topicId}
          eventIds={config.eventIds}
          event={props.event}
          targetLocation={config.targetLocation}
        />
      );
    },
    props: {},
    extractParams: true,
  },
];
